/**
 * Projection worker (RFC 0219 E2: objects & projections).
 *
 * Promotes `projectObjects` from a pure function to a long-lived worker. It
 * watches `mod.object_*` extension entries and maintains the materialized
 * object/file view: on every change it re-derives the view in causal order and
 * hands it to the `ObjectViewSink` (the host boundary that persists the view +
 * writes to disk via the existing file-sync adapters). This generalizes RFC
 * 0206's file-specific `fileDecisions`/materializer to any object type — files
 * are simply `objectType: 'file'` — so `fileHeads` becomes a read of this
 * projection rather than a stored source of truth.
 *
 * Coordination shape matches the inference/tool workers: a `match` predicate
 * wakes it, and `drain` is idempotent + re-entrant. The worker dedupes by the
 * serialized view, so an unchanged log (or its own non-log sink writes) settles
 * in one empty drain instead of republishing. The sink writes to the branch
 * doc / disk, NOT back to this session log, so a publish never re-wakes the
 * worker — but the dedupe makes that robust regardless.
 */

import type { BranchLog } from '../log/branch-log'
import type { ObjectRef } from '../log/projection-objects'
import type { Entry } from '../log/types'
import { OBJECT_EXTENSION } from '../log/types'
import type { Worker } from './contract'
import type { ObjectViewSink } from './seams'

const OBJECT_EXTENSION_TYPES = new Set<string>([
  OBJECT_EXTENSION.created,
  OBJECT_EXTENSION.edited,
  OBJECT_EXTENSION.deleted,
])

/** Stable serialization of the view for change detection (sorted by path). */
function serializeView(view: Map<string, ObjectRef>): string {
  const rows = [...view.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([path, ref]) => `${path} ${ref.objectType} ${ref.docId}`)
  return rows.join('\n')
}

export class ProjectionWorker implements Worker {
  readonly kind = 'custom' as const
  readonly match = (e: Entry): boolean =>
    e.payload.type === 'extension' &&
    OBJECT_EXTENSION_TYPES.has(e.payload.payload.extensionType)

  // Last view we published, for re-entrant dedupe. Seeded with the empty-view
  // serialization so an empty log at startup is a no-op, while a genuine
  // empty-transition (everything deleted after being non-empty) still
  // publishes the now-empty view.
  private lastPublished = serializeView(new Map())

  constructor(private readonly sink: ObjectViewSink) {}

  async drain(log: BranchLog): Promise<number> {
    const view = log.projectObjects()
    const key = serializeView(view)
    if (key === this.lastPublished) return 0
    await this.sink.publish(view)
    // Mark published only after the sink succeeds, so a throwing sink retries
    // on the next change rather than silently dropping the view.
    this.lastPublished = key
    return view.size
  }
}
